package fr.labodoc.healthProfessional.webApp.pages.dashboard.components

import fr.labodoc.healthProfessional.webApp.data.repository.MedicinesRepository
import fr.labodoc.utils.normalize
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class MedicineListViewModelImpl(
  private val atcClassificationCodeFilter: String?,
  private val medicineRepository: MedicinesRepository,
  private val viewModelScope: CoroutineScope
) : MedicineListViewModel() {
  override val specialityThematics: MutableStateFlow<List<Thematic>> = MutableStateFlow(emptyList())
  override val interspecialityThematics: MutableStateFlow<List<Thematic>> = MutableStateFlow(emptyList())
  override val atcClassifications: MutableStateFlow<List<AtcClassification>> = MutableStateFlow(emptyList())
  override val searchBarResults: MutableStateFlow<Set<SearchBarResult>> = MutableStateFlow(emptySet())
  override val filter: MutableStateFlow<Filter?> = MutableStateFlow(null)
  override val alphabeticalSort: MutableStateFlow<Boolean> = MutableStateFlow(false)

  private val medicines: MutableStateFlow<List<Medicine>> = MutableStateFlow(emptyList())

  private val state: MutableStateFlow<State> by lazy {
    start()
    MutableStateFlow(State.Loading)
  }

  override val medicineListState: StateFlow<MedicineListState> = combine(state, medicines, filter, alphabeticalSort) { state, medicines, filter, alphabeticalSort ->
    when (state) {
      State.Loading -> MedicineListState.Loading
      State.Error -> MedicineListState.Error
      State.Loaded -> MedicineListState.Loaded(medicines.filter(filter).sort(alphabeticalSort))
    }
  }.stateIn(viewModelScope, SharingStarted.Lazily,MedicineListState.Loading)

  override val medicinesFilteredCount: StateFlow<Int?> = combine(medicineListState, filter) { medicineListState, filter ->
    filter?.let {
      if (medicineListState is MedicineListState.Loaded)
        medicineListState.medicines.count()
      else
        null
    }
  }.stateIn(viewModelScope, SharingStarted.Lazily, null)


  private sealed class State {
    data object Loading : State()
    data object Error : State()
    data object Loaded : State()
  }

  fun start() {
    viewModelScope.launch {
      state.update {
        State.Loading
      }

      medicineRepository
        .getMedicines()
        .onLeft {
          state.update {
            State.Error
          }
        }
        .onRight { response ->
          medicines.update {
            response.medicines
              .mapIndexed { index, medicine ->
                Medicine(
                  index = index,
                  id = medicine.id,
                  laboratory = medicine.laboratory?.let { laboratory ->
                    Medicine.Laboratory(
                      name = laboratory.name
                    )
                  },
                  name = medicine.name,
                  normalizedName = medicine.name.normalize().lowercase(),
                  logoUrl = medicine.logoUrl,
                  atcClassificationCode = medicine.atcClassification.code,
                  dci = medicine.atcClassification.name,
                  normalizedDci = medicine.atcClassification.name.normalize().lowercase(),
                  atcClassificationCategory = (medicine.atcClassification.getLevelOrHigher(4)
                    ?: medicine.atcClassification).let { atcClassification ->
                    Medicine.AtcClassification(
                      code = atcClassification.code,
                      name = atcClassification.shortName ?: atcClassification.name
                    )
                  },
                  hasRecentMarketingDocument = medicine.hasRecentMarketingDocument,
                  displayed = true
                )
              }
          }

          specialityThematics.update {
            response.specialityThematics
              .map { thematic ->
                Thematic(
                  id = thematic.id,
                  name = thematic.name,
                  atcClassificationCodes = thematic.atcClassificationsCodes,
                  numberOfMedicines = thematic.numberOfMedicines,
                )
              }
              .groupBy { it.atcClassificationCodes.firstOrNull()?.first() ?: 'Z' }
              .entries
              .sortedBy { it.key }
              .flatMap { (_, group) ->
                group.sortedBy(Thematic::name)
              }
          }

          interspecialityThematics.update {
            response.interspecialityThematics
              .map { thematic ->
                Thematic(
                  id = thematic.id,
                  name = thematic.name,
                  atcClassificationCodes = thematic.atcClassificationsCodes,
                  numberOfMedicines = thematic.numberOfMedicines,
                )
              }
              .groupBy { it.atcClassificationCodes.firstOrNull()?.first() ?: 'Z' }
              .entries
              .sortedBy { it.key }
              .flatMap { (_, group) ->
                group.sortedBy(Thematic::name)
              }
          }

          atcClassifications.update {
            response.atcClassifications
              .map { atcClassification ->
                AtcClassification(
                  code = atcClassification.code,
                  name = atcClassification.shortName ?: atcClassification.name,
                  numberOfMedicines = atcClassification.numberOfMedicines,
                  parentCode = atcClassification.parent?.code
                )
              }
              .sortedBy { it.code }
          }

          if (atcClassificationCodeFilter != null) {
            updateAtcClassification(atcClassificationCodeFilter)
          }

          state.update {
            State.Loaded
          }
        }
    }
  }

  override fun refresh() {
    viewModelScope.launch {
      state.update {
        State.Loading
      }

      medicineRepository
        .getMedicines()
        .onLeft {
          state.update {
            State.Error
          }
        }
        .onRight { response ->
          medicines.update {
            response.medicines
              .mapIndexed { index, medicine ->
                Medicine(
                  index = index,
                  id = medicine.id,
                  laboratory = medicine.laboratory?.let { laboratory ->
                    Medicine.Laboratory(
                      name = laboratory.name
                    )
                  },
                  name = medicine.name,
                  normalizedName = medicine.name.normalize().lowercase(),
                  logoUrl = medicine.logoUrl,
                  atcClassificationCode = medicine.atcClassification.code,
                  dci = medicine.atcClassification.name,
                  normalizedDci = medicine.atcClassification.name.normalize().lowercase(),
                  atcClassificationCategory = (medicine.atcClassification.getLevelOrHigher(4)
                    ?: medicine.atcClassification).let { atcClassification ->
                    Medicine.AtcClassification(
                      code = atcClassification.code,
                      name = atcClassification.shortName ?: atcClassification.name
                    )
                  },
                  hasRecentMarketingDocument = medicine.hasRecentMarketingDocument,
                  displayed = true
                )
              }
          }

          specialityThematics.update {
            response.specialityThematics
              .map { thematic ->
                Thematic(
                  id = thematic.id,
                  name = thematic.name,
                  atcClassificationCodes = thematic.atcClassificationsCodes,
                  numberOfMedicines = thematic.numberOfMedicines,
                )
              }
              .groupBy { it.atcClassificationCodes.firstOrNull()?.first() ?: 'Z' }
              .entries
              .sortedBy { it.key }
              .flatMap { (_, group) ->
                group.sortedBy(Thematic::name)
              }
          }

          interspecialityThematics.update {
            response.interspecialityThematics
              .map { thematic ->
                Thematic(
                  id = thematic.id,
                  name = thematic.name,
                  atcClassificationCodes = thematic.atcClassificationsCodes,
                  numberOfMedicines = thematic.numberOfMedicines,
                )
              }
              .groupBy { it.atcClassificationCodes.firstOrNull()?.first() ?: 'Z' }
              .entries
              .sortedBy { it.key }
              .flatMap { (_, group) ->
                group.sortedBy(Thematic::name)
              }
          }

          atcClassifications.update {
            response.atcClassifications
              .map { atcClassification ->
                AtcClassification(
                  code = atcClassification.code,
                  name = atcClassification.shortName ?: atcClassification.name,
                  numberOfMedicines = atcClassification.numberOfMedicines,
                  parentCode = atcClassification.parent?.code
                )
              }
              .sortedBy { it.code }
          }

          state.update {
            State.Loaded
          }
        }
    }
  }

  override fun resetFilter() {
    filter.update {
      null
    }
  }

  override fun updateSearchBarInput(searchBarInput: String?) {
    filter.value = searchBarInput?.let {
      Filter.Search(
        input = searchBarInput
      )
    }

    viewModelScope.launch {
      val normalizedSearchBarInput = searchBarInput?.normalize()?.lowercase()

      searchBarResults.value = normalizedSearchBarInput?.let {
        medicines.value
          .fold(mutableSetOf<SearchBarResult>()) { searchBarResults, medicine ->
            val dciMatch = medicine.normalizedDci.contains(normalizedSearchBarInput)
            val nameMatch = medicine.normalizedName.contains(normalizedSearchBarInput)

            if (dciMatch) {
              searchBarResults += SearchBarResult(medicine.dci, false)
              if (medicine.normalizedName != medicine.normalizedDci)
                searchBarResults += SearchBarResult(medicine.name, medicine.laboratory != null)
            }

            if (nameMatch && medicine.normalizedName != medicine.normalizedDci)
              searchBarResults += SearchBarResult(medicine.name, medicine.laboratory != null)

            searchBarResults
          }
      }.orEmpty()
    }
  }

  override fun updateAtcClassification(code: String) {
    filter.update {
      val atcClassification = atcClassifications.value.find { it.code == code }

      atcClassification?.let {
        when (val currentFilter = filter.value) {
          is Filter.AtcClassification -> {
            if (currentFilter.code == code) {
              atcClassifications.value
                .find { it.code == atcClassification.parentCode }
                ?.let {
                  Filter.AtcClassification(
                    code = it.code,
                    name = it.name,
                    numberOfMedicines = it.numberOfMedicines,
                  )
                }
            } else {
              Filter.AtcClassification(
                code = atcClassification.code,
                name = atcClassification.name,
                numberOfMedicines = atcClassification.numberOfMedicines,
              )
            }
          }

          else -> {
            Filter.AtcClassification(
              code = atcClassification.code,
              name = atcClassification.name,
              numberOfMedicines = atcClassification.numberOfMedicines
            )
          }
        }
      }
    }
  }

  override fun updateThematic(thematicId: String?) {
    filter.update {
      val thematic = specialityThematics.value.find { it.id == thematicId } ?: interspecialityThematics.value.find { it.id == thematicId }

      thematic?.let {
        Filter.Thematic(
          id = thematic.id,
          name = thematic.name,
          atcClassificationCodes = thematic.atcClassificationCodes,
          numberOfMedicines = thematic.numberOfMedicines
        )
      }
    }
  }

  override fun updateAlphabeticalSort(alphabeticalSort: Boolean) {
    this@MedicineListViewModelImpl.alphabeticalSort.update {
      alphabeticalSort
    }
  }

  private fun List<Medicine>.filter(filter: Filter?): List<Medicine> {
    return when (filter) {
      is Filter.Search -> {
        val normalizedFilterInput = filter.input.normalize().lowercase()

        this
          .filter { medicine ->
            val nameMatch = medicine.normalizedName.contains(normalizedFilterInput)
            val dciMatch = medicine.normalizedDci.contains(normalizedFilterInput)

            nameMatch || dciMatch
          }
      }

      is Filter.AtcClassification -> {
        this
          .filter { medicine ->
            medicine.atcClassificationCode.startsWith(filter.code, true)
          }
      }

      is Filter.Thematic -> {
        this
          .filter { medicine ->
            filter.atcClassificationCodes
              .any { atcClassificationCode ->
                medicine.atcClassificationCode.startsWith(atcClassificationCode, true)
              }
          }
      }

      is Filter.RecentMarketingDocuments -> {
        this
          .filter { medicine ->
            medicine.hasRecentMarketingDocument
          }
      }

      null -> {
        val specialityThematics = specialityThematics.value

        if (specialityThematics.isNotEmpty()) {
          this
            .filter { medicine ->
              specialityThematics.any { specialityThematic ->
                specialityThematic.atcClassificationCodes.any { atcClassificationCode ->
                  medicine.atcClassificationCode.startsWith(atcClassificationCode, true)
                }
              }
            }
        } else
          this
      }
    }
  }

  private fun List<Medicine>.sort(alphabeticalSort: Boolean): List<Medicine> {
    return if (alphabeticalSort)
      this.sortedBy(Medicine::name)
    else
      this.sortedBy(Medicine::index)
  }
}
